import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from './ThemeContext';
import { ArrowUpRight, LogOut } from 'lucide-react';
import { api } from './api';

const Admin = () => {
  const [pages, setPages] = useState([]);
  const [newPage, setNewPage] = useState({ title: '', url: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const theme = useTheme();

  useEffect(() => {
    const checkAuth = async () => {
      const authed = await api.isAuthenticated();
      if (!authed) {
        navigate('/login');
        return;
      }
      loadPages();
    };

    checkAuth();
  }, [navigate]);

  const loadPages = async () => {
    try {
      setLoading(true);
      const response = await api.getPages();
      if (response.success) {
        setPages(response.pages);
      }
    } catch (err) {
      setError('Fehler beim Laden der Pages: ' + err.message);
      console.error('Load pages error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    await api.logout();
    navigate('/');
  };

  const handleAddPage = async (e) => {
    e.preventDefault();
    if (!newPage.title || !newPage.url) {
      setError('Bitte Titel und URL eingeben');
      return;
    }

    try {
      setLoading(true);
      setError('');
      const response = await api.addPage(newPage.title, newPage.url);
      
      if (response.success) {
        setNewPage({ title: '', url: '' });
        await loadPages(); // Reload pages
      }
    } catch (err) {
      setError('Fehler beim Hinzufügen: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDeletePage = async (id) => {
    if (!window.confirm('Möchtest du diese Page wirklich löschen?')) {
      return;
    }

    try {
      setLoading(true);
      setError('');
      await api.deletePage(id);
      await loadPages(); // Reload pages
    } catch (err) {
      setError('Fehler beim Löschen: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handlePushPage = (page) => {
    // Open page in new tab
    window.open(page.url, '_blank');
  };

  return (
    <div className={`min-h-screen ${theme.colors.bg} ${theme.colors.text} font-mono p-8`}>
      <div className="max-w-4xl mx-auto">
        <div className="flex justify-between items-center mb-8">
          <h1 className={`text-4xl font-mono ${theme.colors.text}`}>Admin Panel</h1>
          <button
            onClick={handleLogout}
            className={`header-button font-mono ${theme.isDarkMode ? 'header-button-dark' : ''} flex items-center gap-2`}
          >
            <span>Abmelden</span>
            <LogOut size={14} />
          </button>
        </div>

        {error && (
          <div className={`mb-4 p-4 rounded-lg ${theme.isDarkMode ? 'bg-red-900/30 text-red-300' : 'bg-red-100 text-red-800'}`}>
            {error}
          </div>
        )}

        <div className={`mb-8 p-6 border ${theme.colors.border} rounded-lg`}>
          <h2 className={`text-2xl font-mono mb-4 ${theme.colors.text}`}>Neue Page hinzufügen</h2>
          <form onSubmit={handleAddPage} className="space-y-4">
            <div>
              <label className={`block mb-2 ${theme.colors.text}`}>Titel</label>
              <input
                type="text"
                value={newPage.title}
                onChange={(e) => setNewPage({ ...newPage, title: e.target.value })}
                className={`w-full px-4 py-2 border ${theme.colors.border} ${theme.colors.bg} ${theme.colors.text} rounded focus:outline-none`}
                placeholder="z.B. Test Page 1"
                required
              />
            </div>
            <div>
              <label className={`block mb-2 ${theme.colors.text}`}>URL</label>
              <input
                type="url"
                value={newPage.url}
                onChange={(e) => setNewPage({ ...newPage, url: e.target.value })}
                className={`w-full px-4 py-2 border ${theme.colors.border} ${theme.colors.bg} ${theme.colors.text} rounded focus:outline-none`}
                placeholder="https://example.com"
                required
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              className={`header-button font-mono ${theme.isDarkMode ? 'header-button-dark' : ''} flex items-center gap-2 ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              <span>{loading ? 'Wird hinzugefügt...' : 'Hinzufügen'}</span>
              {!loading && <ArrowUpRight size={14} />}
            </button>
          </form>
        </div>

        <div>
          <h2 className={`text-2xl font-mono mb-4 ${theme.colors.text}`}>Gespeicherte Pages</h2>
          {loading && pages.length === 0 ? (
            <p className={theme.colors.textOpacity}>Lade Pages...</p>
          ) : pages.length === 0 ? (
            <p className={theme.colors.textOpacity}>Noch keine Pages hinzugefügt.</p>
          ) : (
            <div className="space-y-4">
              {pages.map((page) => (
                <div
                  key={page.id}
                  className={`p-4 border ${theme.colors.border} rounded-lg flex justify-between items-center`}
                >
                  <div>
                    <h3 className={`text-lg font-mono ${theme.colors.text}`}>{page.title}</h3>
                    <p className={`text-sm ${theme.colors.textOpacity}`}>{page.url}</p>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => handlePushPage(page)}
                      className={`header-button font-mono text-sm ${theme.isDarkMode ? 'header-button-dark' : ''} flex items-center gap-2`}
                    >
                      <span>Öffnen</span>
                      <ArrowUpRight size={12} />
                    </button>
                    <button
                      onClick={() => handleDeletePage(page.id)}
                      disabled={loading}
                      className={`px-4 py-2 border ${theme.colors.border} ${theme.colors.text} ${theme.colors.hoverBg} ${theme.colors.hoverText} transition-all font-mono text-sm ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      {loading ? '...' : 'Löschen'}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Admin;

